import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { signUpUrl, uploadUrl, feesUrl, loginUrl } from "../../util/url";
import { useAuthStore } from "../../store/useAuthStore";
import { useUploadGuard } from "../../hook/useUploadGuard";
import "./Fees.css";
import PropTypes from "prop-types";

/**
 * STATIC CONTENT
 */
const PAGE_TITLE = "판매 수수료 0원 · 선택형 수수료 안내 | 후루츠패밀리";
const PAGE_DESC =
  "후루츠패밀리 판매 수수료는 선택형입니다. 판매자의 선택으로 구매자 또는 판매자가 부담하도록 설정할 수 있으며, 안전결제·분쟁 조정 등 믿고거래를 위한 안심 서비스가 포함됩니다. 가치 있는 옷을 후루에서 부담 없이 판매하고, 수수료 계산기로 정산 금액을 미리 확인해 보세요.";

const FEATURE_IMAGES = [
  {
    src: "https://image.production.fruitsfamily.com/public/collection/resized%40width1125/y2MSp67eg-110.png",
    alt: "선택형 수수료 - 유연한 수수료",
    width: 1125,
    height: 1125,
  },
  {
    src: "https://image.production.fruitsfamily.com/public/collection/resized%40width1125/Zux_aWl7M7-111.png",
    alt: "선택형 수수료 - 스마트 노출",
    width: 1125,
    height: 1125,
  },
  {
    src: "https://image.production.fruitsfamily.com/public/collection/resized%40width1125/TASrokdEL-113%281%29.png",
    alt: "선택형 수수료 - 안전결제",
    width: 1125,
    height: 1125,
  },
];

/**
 * FEE LOGIC
 */
export function computeFees(totalPrice) {
  const t = Math.max(0, Math.round(totalPrice));
  if (t === 0) return null;
  const fee385 = Math.round(t * 0.0385);
  return {
    zero: { sellerAmount: t, buyerPrice: t + fee385 }, // buyer pays 3.85%
    fruitsfamily: { sellerAmount: t - fee385, buyerPrice: t }, // seller pays 3.85%
  };
}

/**
 * JSON-LD HELPERS
 */
const jsonLdWebPage = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: PAGE_TITLE,
  description: PAGE_DESC,
  url: `https://fruitsfamily.co.kr${feesUrl()}`,
  inLanguage: "ko-KR",
  isPartOf: {
    "@type": "WebSite",
    name: "후루츠패밀리",
    url: "https://fruitsfamily.co.kr",
  },
};

const jsonLdBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "홈",
      item: "https://fruitsfamily.co.kr",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "판매하기",
      item: "https://fruitsfamily.co.kr/sell",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "판매 수수료",
      item: `https://fruitsfamily.co.kr${feesUrl()}`,
    },
  ],
};

const jsonLdFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "선택형 수수료는 뭔가요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "'선택형 수수료'는 판매자가 거래 수수료를 누가 부담할지 직접 선택할 수 있는 기능입니다. 상품을 등록할 때 수수료 부담 방식을 자유롭게 정할 수 있어요.",
      },
    },
    {
      "@type": "Question",
      name: "수수료가 아예 없는 건가요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "거래 수수료는 반드시 발생합니다. 판매자 또는 구매자 중 한 명이 부담해야 하며, 선택에 따라 정산 방식 또는 결제 금액이 달라집니다.",
      },
    },
    {
      "@type": "Question",
      name: "수수료에는 어떤 서비스 비용이 포함되나요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "거래 수수료에는 다음과 같은 안심 서비스 이용료가 포함됩니다: 중고거래 중 발생할 수 있는 상황에 대한 보호, 분쟁 발생 시 고객센터의 조정 및 지원, 거래 모니터링 및 안전결제 시스템 운영.",
      },
    },
    {
      "@type": "Question",
      name: "이 정책이 판매자에게는 어떤 의미일까요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "판매자가 수수료를 부담할 경우 수수료가 판매가에서 차감되어 정산되며, 판매가는 낮아져 더 빠르게 판매될 수 있습니다. 구매자에게 부담시키는 경우 수수료가 결제 금액에 추가되고 판매가는 그대로 정산되어 100%를 수령할 수 있습니다.",
      },
    },
    {
      "@type": "Question",
      name: "이 정책이 구매자에게는 어떤 의미일까요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "판매자 부담 시 수수료가 포함되지 않아 더 저렴한 가격에 구매할 수 있습니다. 구매자 부담 시 결제 금액에 수수료가 추가되며, 거래 중 문제가 생기면 안심 서비스를 이용할 수 있습니다.",
      },
    },
    {
      "@type": "Question",
      name: "판매자가 수수료를 바꾸는 건 언제 가능한가요?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "수수료 부담 방식은 상품 등록 시 설정되며, 이후 수정은 상품 수정 페이지에서 가능합니다.",
      },
    },
  ],
};

export default function Fees({ userAgent }) {
  const navigate = useNavigate();
  const { isLogin } = useAuthStore();
  const { guardUploadClick } = useUploadGuard(userAgent);

  const handleUploadClick = (e) =>
    guardUploadClick(e, {
      onAllowed: () => {
        if (!isLogin) {
          navigate(loginUrl());
          return;
        }
        navigate(uploadUrl());
      },
    });
  const [productPrice, setProductPrice] = useState("");
  const [shippingFee, setShippingFee] = useState("");

  const price = Math.max(0, parseFloat(productPrice) || 0);
  const shipping = Math.max(0, parseFloat(shippingFee) || 0);
  const totalPrice = price + shipping;
  const results = useMemo(() => computeFees(totalPrice), [totalPrice]);

  return (
    <div className="Fees container">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href={`https://fruitsfamily.co.kr${feesUrl()}`} />
        <link
          rel="alternate"
          hrefLang="ko-KR"
          href={`https://fruitsfamily.co.kr${feesUrl()}`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://fruitsfamily.co.kr${feesUrl()}`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="후루츠패밀리" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta
          property="og:url"
          content={`https://fruitsfamily.co.kr${feesUrl()}`}
        />
        <meta
          property="og:image"
          content="https://image.production.fruitsfamily.com/public/collection/resized%40width620/Nbr7v29QZ-img_sell_header.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        <meta
          name="twitter:image"
          content="https://image.production.fruitsfamily.com/public/collection/resized%40width620/Nbr7v29QZ-img_sell_header.png"
        />
        <link rel="preload" as="image" href={FEATURE_IMAGES[0].src} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }}
        />
      </Helmet>

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="Fees-breadcrumb">
        <ol className="Fees-breadcrumb-list">
          <li>
            <Link to="/" className="Fees-breadcrumb-link">
              홈
            </Link>
          </li>
          <li aria-hidden>›</li>
          <li>
            <Link to="/sell" className="Fees-breadcrumb-link">
              판매하기
            </Link>
          </li>
          <li aria-hidden>›</li>
          <li aria-current="page" className="Fees-breadcrumb-current">
            판매 수수료
          </li>
        </ol>
      </nav>

      <main id="main-content">
        {/* Hero Section */}
        <section className="Fees-hero">
          <div className="Fees-hero-container">
            <h1 className="Fees-hero-title">판매 수수료 영원히 0원</h1>
            <p className="Fees-hero-subtitle">
              가치 있는 옷은 후루츠패밀리에서 부담없이 판매하세요
            </p>
            <div className="Fees-hero-actions">
              <button
                className="Fees-hero-button-primary"
                onClick={handleUploadClick}
              >
                지금 판매하기
              </button>
              <Link className="sr-only" to={signUpUrl()}>
                전문 판매자이세요?
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="Fees-features">
          <div className="Fees-features-container">
            <h2 className="Fees-features-title">선택형 수수료 핵심</h2>
            <p className="Fees-features-subtitle">
              판매 수수료를 판매자 또는 구매자 중<br />
              누가 부담할지 직접 선택할 수 있어요
            </p>

            <div className="Fees-features-grid">
              {FEATURE_IMAGES.map((image, index) => (
                <article key={index} className="Fees-feature-card">
                  <h3 className="Fees-feature-title">
                    {index === 0 && (
                      <>
                        거래 수수료,
                        <br />
                        이제 직접 선택하세요
                      </>
                    )}
                    {index === 1 && (
                      <>
                        판매가의 100%
                        <br />
                        정산 받으세요
                      </>
                    )}
                    {index === 2 && (
                      <>
                        빠르고 안전한
                        <br />
                        거래를 선택하세요
                      </>
                    )}
                  </h3>
                  <p className="Fees-feature-desc">
                    {index === 0 && "배송비처럼 누가 부담할지 선택할 수 있어요"}
                    {index === 1 && "내가 부담하여 빠르게 판매할 수도 있어요"}
                    {index === 2 && (
                      <>
                        안전결제 이용 시고객 지원 혜택을
                        <br />
                        받을 수 있어요
                      </>
                    )}
                  </p>
                  <div className="Fees-feature-image">
                    <img
                      className="Fees-feature-img"
                      src={image.src}
                      alt={image.alt}
                      loading="lazy"
                      decoding="async"
                      width={image.width}
                      height={image.height}
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="Fees-calculator">
          <div className="Fees-calculator-container">
            <h2 className="Fees-calculator-title">수수료 계산기</h2>
            <p className="Fees-calculator-subtitle">
              상품 가격과 배송비를 입력하여 수수료 옵션별
              <br />
              정산 금액을 비교해 보세요
            </p>

            <form
              className="Fees-calculator-form"
              onSubmit={(e) => e.preventDefault()}
              aria-labelledby="calc-label"
            >
              <span id="calc-label" className="Fees-sr-only">
                수수료 계산기 입력 폼
              </span>
              <div className="Fees-input-group">
                <label className="Fees-input-label" htmlFor="productPrice">
                  상품 가격
                </label>
                <div className="Fees-input-wrapper">
                  <input
                    id="productPrice"
                    name="productPrice"
                    type="text"
                    inputMode="numeric"
                    aria-describedby="productPriceHelp"
                    value={productPrice.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, "");
                      if (digits === "") {
                        setProductPrice("");
                        return;
                      }
                      const clamped = Math.min(10000000, parseInt(digits, 10));
                      setProductPrice(String(clamped));
                    }}
                    placeholder="예: 50,000"
                    className="Fees-input"
                    autoComplete="off"
                  />
                  <span className="Fees-input-suffix">원</span>
                </div>
                <p id="productPriceHelp" className="Fees-input-help">
                  상품 판매가를 입력하세요.
                </p>
              </div>

              <div className="Fees-input-group">
                <label className="Fees-input-label" htmlFor="shippingFee">
                  배송비
                </label>
                <div className="Fees-input-wrapper">
                  <input
                    id="shippingFee"
                    name="shippingFee"
                    type="text"
                    inputMode="numeric"
                    aria-describedby="shippingFeeHelp"
                    value={shippingFee.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, "");
                      if (digits === "") {
                        setShippingFee("");
                        return;
                      }
                      const clamped = Math.min(1000000, parseInt(digits, 10));
                      setShippingFee(String(clamped));
                    }}
                    placeholder="예: 3,000"
                    className="Fees-input"
                    autoComplete="off"
                  />
                  <span className="Fees-input-suffix">원</span>
                </div>
                <p id="shippingFeeHelp" className="Fees-input-help">
                  예상 배송비를 입력하세요.
                </p>
              </div>
            </form>

            {results && (
              <div className="Fees-results">
                <div className="Fees-results-header">
                  <span className="Fees-results-badge">
                    누가 수수료를 낼지, 판매자가 정할 수 있어요
                  </span>
                </div>
                <div className="Fees-results-grid">
                  {Object.entries(results).map(
                    ([key, { sellerAmount, buyerPrice }]) => (
                      <article
                        key={key}
                        className={`Fees-result-card ${key === "zero" ? "Fees-result-card-highlight" : ""}`}
                      >
                        <div className="Fees-result-header">
                          <h3 className="Fees-result-title">
                            {key === "zero" && "0% 수수료 (구매자 부담)"}
                            {key === "fruitsfamily" &&
                              "3.85% 수수료 (판매자 부담)"}
                          </h3>
                          <span
                            className={`Fees-result-badge ${key === "zero" ? "Fees-result-badge-blue" : "Fees-result-badge-gray"}`}
                          >
                            {key === "zero" ? "구매자 부담" : "판매자 부담"}
                          </span>
                        </div>
                        <div className="Fees-result-details">
                          <div className="Fees-result-row">
                            <span className="Fees-result-label">
                              판매자 정산금액
                            </span>
                            <strong className="Fees-result-value">
                              {new Intl.NumberFormat("ko-KR").format(
                                sellerAmount,
                              )}
                              원
                            </strong>
                          </div>
                          <div className="Fees-result-row">
                            <span className="Fees-result-label">
                              구매자 결제금액
                            </span>
                            <span className="Fees-result-value">
                              {new Intl.NumberFormat("ko-KR").format(
                                buyerPrice,
                              )}
                              원
                            </span>
                          </div>
                        </div>
                      </article>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="Fees-faq">
          <div className="Fees-faq-container">
            <h2 className="Fees-faq-title">자주 묻는 질문</h2>
            <div className="Fees-faq-grid">
              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">선택형 수수료는 뭔가요?</h3>
                <p className="Fees-faq-answer">
                  &apos;선택형 수수료&apos;는 판매자가 거래 수수료를{" "}
                  <strong>누가 부담할지</strong> 직접 선택할 수 있는 기능입니다.
                  상품을 등록할 때, 수수료 부담 방식을 자유롭게 정할 수 있어요.
                </p>
              </article>

              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">
                  수수료가 아예 없는 건가요?
                </h3>
                <p className="Fees-faq-answer">
                  거래 수수료는 <strong>반드시 발생</strong>합니다. 판매자 또는
                  구매자 중 한 명이 부담해야 하며, 선택에 따라 정산 방식 또는
                  결제 금액이 달라집니다.
                </p>
              </article>

              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">
                  수수료에는 어떤 서비스 비용이 포함되나요?
                </h3>
                <p className="Fees-faq-answer">
                  거래 수수료에는 다음과 같은{" "}
                  <strong>안심 서비스 이용료</strong>가 포함됩니다:
                </p>
                <ul className="Fees-faq-list">
                  <li>중고거래 중 발생할 수 있는 상황에 대한 보호</li>
                  <li>분쟁 발생 시 고객센터의 조정 및 지원</li>
                  <li>거래 모니터링 및 안전결제 시스템 운영</li>
                </ul>
              </article>

              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">
                  이 정책이 판매자에게는 어떤 의미일까요?
                </h3>
                <p className="Fees-faq-answer">
                  <strong>판매자가 수수료를 부담할 경우:</strong>
                </p>
                <ul className="Fees-faq-list">
                  <li>수수료가 판매가에서 차감되어 정산됩니다</li>
                  <li>상품 가격이 낮아져 더 빠르게 판매될 수 있습니다</li>
                </ul>
                <p className="Fees-faq-answer">
                  <strong>구매자에게 부담시키는 경우:</strong>
                </p>
                <ul className="Fees-faq-list">
                  <li>수수료가 결제 금액에 추가되며,</li>
                  <li>판매가는 그대로 정산되어 100%를 수령할 수 있습니다</li>
                </ul>
              </article>

              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">
                  이 정책이 구매자에게는 어떤 의미일까요?
                </h3>
                <p className="Fees-faq-answer">
                  <strong>판매자 부담 시:</strong>
                </p>
                <ul className="Fees-faq-list">
                  <li>
                    수수료가 포함되지 않아 더 저렴한 가격에 구매할 수 있습니다
                  </li>
                </ul>
                <p className="Fees-faq-answer">
                  <strong>구매자 부담 시:</strong>
                </p>
                <ul className="Fees-faq-list">
                  <li>결제 금액에 수수료가 추가되며,</li>
                  <li>
                    거래 중 문제가 생기면 안심 서비스를 이용할 수 있습니다
                  </li>
                </ul>
              </article>

              <article className="Fees-faq-item">
                <h3 className="Fees-faq-question">
                  판매자가 수수료를 바꾸는 건 언제 가능한가요?
                </h3>
                <p className="Fees-faq-answer">
                  수수료 부담 방식은 <strong>상품 등록 시</strong> 설정되며,
                  이후 수정은 <strong>상품 수정 페이지</strong>에서 가능합니다.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>

      {/* Final CTA Footer */}
      <footer className="Fees-footer">
        <div className="Fees-footer-container">
          <h2 className="Fees-footer-title">지금 바로 판매 해볼까요?</h2>
          <p className="Fees-footer-desc">
            수수료는 선택, 정산은 투명. 후루츠패밀리에서 쉽고 빠르게 판매하세요.
          </p>
          <div className="Fees-footer-buttons">
            <button
              onClick={handleUploadClick}
              className="Fees-footer-button-primary"
            >
              판매 시작하기
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

Fees.propTypes = {
  userAgent: PropTypes.string.isRequired,
};
